import React, { useMemo } from "react";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import { Classroom } from "@models/ISchema";
import { Link, Select } from "@foris/avocado-suite";
import UsableLabel from "@common/components/Autocomplete/usabe-label";
import SimpleResourceList from "@modules/sections/EditSessions/components/simple-resource-list";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import { getClassroomLocationCode } from "@modules/sections/EditSessions/utils/getClassroomLocationCode";
import { ClassroomOption } from "../../hooks/useClassroomSearch";
import styles from "./classroomPicker.module.scss";

interface ClassroomPickerProps {
  classrooms: Classroom[];
  disabled?: boolean;
  loading?: boolean;
  options: ClassroomOption[];
  someDeletedSessionIsSelected?: boolean;
  isReadOnly?: boolean;
  onSearch: (value: string) => void;
  onSelect: (classroom: ClassroomOption) => void;
  onDelete: (classroom: Classroom) => void;
  onAdvancedSearch?: () => void;
}

const getFormattedLabel = (option: unknown) => {
  const data = option as ClassroomOption;
  return (
    <UsableLabel
      label={`${data.code} - ${data.name} - ${data?.building?.campus?.code || ""}`}
      isUsable={data?.usable}
    />
  );
};

const ClassroomPicker = ({
  classrooms,
  disabled = false,
  loading = false,
  options,
  someDeletedSessionIsSelected = false,
  isReadOnly = false,
  onSearch,
  onSelect,
  onDelete,
  onAdvancedSearch,
}: ClassroomPickerProps) => {
  const { t } = useTranslation();
  const { editorUrl } = useNavigationUrl();

  const adaptedClassrooms = useMemo(() => {
    if (isReadOnly && !classrooms?.length) {
      return [
        {
          id: "",
          label: t("assignation-edit.form.notifications.without-assignation"),
          rawData: null,
        },
      ];
    }

    return classrooms?.map(classroom => ({
      id: isReadOnly ? getClassroomLocationCode(classroom) : classroom?.code,
      label: classroom?.name,
      rawData: classroom,
      path: isReadOnly && classroom?.id ? editorUrl("classroom", classroom.id) : undefined,
    }));
  }, [classrooms, isReadOnly, t]);

  const isFieldDisabled = someDeletedSessionIsSelected || disabled;

  return (
    <div className={styles.classroomPicker}>
      {!isReadOnly && (
        <div
          className={cx(
            styles.classroomPicker_fields,
            !adaptedClassrooms?.length && styles.classroomPicker_fields__noSpacing,
            !onAdvancedSearch && styles.classroomPicker_fields__fullWidth,
          )}
        >
          <Select
            isClearable
            disabled={isFieldDisabled}
            isLoading={loading}
            options={options}
            value={null}
            isOptionDisabled={option => !!(option as any)?.isDisabled}
            formatOptionLabel={getFormattedLabel}
            placeholder={t("assignation-edit.form.classroom.selector-placeholder")}
            onInputChange={value => onSearch(value)}
            onChange={(value: ClassroomOption) => {
              if (value) onSelect(value);
            }}
          />

          {onAdvancedSearch && (
            <Link
              className={styles.classroomPicker_fields_fieldLink}
              iconRight="external-link"
              size="sm"
              disabled={isFieldDisabled}
              onClick={onAdvancedSearch}
            >
              {t("assignation-edit.form.classroom.advanced-search")}
            </Link>
          )}
        </div>
      )}

      <SimpleResourceList
        items={adaptedClassrooms}
        isDisabled={isFieldDisabled}
        onDeleteResource={!isReadOnly ? (classroom: Classroom) => onDelete(classroom) : undefined}
      />
    </div>
  );
};

export default ClassroomPicker;
